// Core
import { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { motion, useAnimation } from 'framer-motion';
import { Box, Image, Divider, Button, Grid, Text, Flex, Checkbox, 
    Slider, SliderTrack, SliderFilledTrack, SliderThumb,
    useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link, useHistory, useLocation } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import { ReactComponent as PlusCategoryIcon } from 'assets/svg/show-category-parent.svg';

// Components
import { ArrowCategoryIcon } from 'components/Icons/Icons';
import Partners from '../../Partners';

const Discounts = ({
    discountsRange,
    setDiscountsRange,
}) => {
    const textColor = useColorModeValue('#0E1633', 'white');
    const disColorText = useColorModeValue('#0E1633', '#9FABCB');
    const disColorBg = useColorModeValue('#e2e8f0', '#0A1027');
    const borderBottom = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');

    const [collapsedItems, setCollapsedItems] = useState(true); 
    const toggleItems = () => {
        setCollapsedItems(!collapsedItems);
    };

    const responsiveSettings = useBreakpointValue({ base: true, md: false });
    useEffect(() => { setCollapsedItems(responsiveSettings); }, [responsiveSettings]);

    const history = useHistory();
    const [minVal, setMinVal] = useState(null);
    const [maxVal, setMaxVal] = useState(null);
    const sliderRef = useRef(null);

    const startDrag = (e, type) => {
      e.preventDefault();

      const slider = sliderRef.current;
      const sliderWidth = slider.offsetWidth;

      let newMinVal = minVal || discountsRange[0];
      let newMaxVal = maxVal || discountsRange[1];

      const onMove = (moveEvent) => {
        const rect = slider.getBoundingClientRect();
        const offsetX = moveEvent.clientX - rect.left;
        const percent = Math.max(0, Math.min(100, (offsetX / sliderWidth) * 100));

        if (type === 'min') {
          newMinVal = Math.round(Math.min(percent, newMaxVal - 1));
          setMinVal(newMinVal);
        } else {
          newMaxVal = Math.round(Math.max(percent, newMinVal + 1));
          setMaxVal(newMaxVal);
        }
      };

      const onUp = () => {
        const range = `${newMinVal},${newMaxVal}`;
        setDiscountsRange([newMinVal, newMaxVal]);

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('discount', range);
        history.push({ search: searchParams.toString() });

        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    return (
        <>
            <Flex alignItems="center" justifyContent="space-between" pt="10px">
                <Text variant="text-md" color={textColor} fontWeight="500">
                    Discounts
                </Text>
                <ArrowCategoryIcon 
                    fontSize="24px"
                    transition="0.2s all"
                    transform={ collapsedItems ? 'rotate(90deg)' : 'rotate(0deg)' } 
                    onClick={toggleItems} />
            </Flex>

            <Box 
                maxH={collapsedItems ? '0px' : 'auto'} 
                transition="0.2s all" 
                sx={{
                    '&::-webkit-scrollbar': { display: 'none' }, 
                    '-ms-overflow-style': 'none', 
                    'scrollbar-width': 'none',
                }}
                overflow="hidden" 
                opacity={ collapsedItems ? '0' : '1'}
                p="24px 0px 0 0px">
                <Box w="100%"  borderRadius="lg">

                    <Flex w="100%" gap="10px" textAlign="center">
                        <Box bg={disColorBg} w="100%" py="18px" borderRadius="16px">
                          <Text color={disColorText}>{minVal || discountsRange[0]}%</Text>
                        </Box>
                        <Box bg={disColorBg} w="100%" py="18px" borderRadius="16px">
                          <Text color={disColorText}>{maxVal || discountsRange[1]}%</Text>
                        </Box>
                    </Flex>

                    <Box position="relative" h="40px" mt="10px" ref={sliderRef}>
                        <Box
                            position="absolute"
                            top="50%"
                            left={`${minVal || discountsRange[0]}%`}
                            transform="translate(0, -50%)"
                            w="24px"
                            h="24px"
                            zIndex="2"
                            bg="#0E1633"
                            borderRadius="50%"
                            border="3px solid #099BFF"
                            cursor="pointer"
                            onMouseDown={(e) => startDrag(e, 'min')}
                        />

                        <Box
                            position="absolute"
                            top="50%"
                            left={`${maxVal || discountsRange[1]}%`}
                            transform="translate(-24px, -50%)"
                            w="24px"
                            zIndex="3"
                            h="24px"
                            bg="#0E1633"
                            borderRadius="50%"
                            border="3px solid #099BFF"
                            cursor="pointer"
                            onMouseDown={(e) => startDrag(e, 'max')}
                        />

                        <Box
                          position="absolute"
                          top="50%"
                          left="0"
                          right="0"
                          height="6px"
                          bg="#0A1027"
                          borderRadius="20px"
                          transform="translateY(-50%)"
                        >
                          <Box
                            position="absolute"
                            left={`${minVal || discountsRange[0]}%`}
                            right={`${100 - (maxVal || discountsRange[1])}%`}
                            height="100%"
                            bg="#099BFF"
                            borderRadius="2px"
                          />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box borderTop={borderBottom} opacity={collapsedItems ? '0' : '1'} />
        </>
    );
};


export default Discounts;